import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadMonarchConnection, refreshMonarchBalances } from '../../../redux/actions/import.js';
import monarchBalanceSummary from '../../../utilities/monarchBalanceSummary.js';
import Button from '../../components/Button.jsx';
import Dropdown from '../../elements/Dropdown.jsx';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import VerticalDots from '../../elements/VerticalDots.jsx';

/**
 * Whether this budget is connected to Monarch, learnt the way the import page learns it: from the
 * batch list's `monarch_connection` (loadMonarchConnection, which never sets the import page's error).
 * Read again whenever the budget changes, since a connection is the budget's own, and not at all
 * before a budget is selected. Undefined until the server has said.
 */
export function useMonarchConnection() {
	const dispatch = useDispatch();
	const budgetId = useSelector((state) => {
		const budget = state.dashboard && state.dashboard.player && state.dashboard.player.active_budget;
		return budget ? budget.id : null;
	});
	useEffect(() => {
		if(budgetId !== null) {
			dispatch(loadMonarchConnection());
		}
	}, [budgetId]);
	return useSelector((state) => (state.importing || {}).monarchConnection);
}

/**
 * Whole seconds until `until` (a Date.now() time), ticking down each second; 0 once it has passed or
 * when there is nothing to wait for.
 */
export function useSecondsUntil(until) {
	const left = () => (until ? Math.max(0, Math.ceil((until - Date.now()) / 1000)) : 0);
	const [seconds, setSeconds] = useState(left);
	useEffect(() => {
		setSeconds(left());
		if(!until) {
			return undefined;
		}
		const timer = setInterval(() => {
			const now = left();
			setSeconds(now);
			if(now === 0) {
				clearInterval(timer);
			}
		}, 1000);
		return () => clearInterval(timer);
	}, [until]);
	return seconds;
}

/**
 * The Accounts page's ⋮ menu, holding "Refresh balances": one request to Monarch for every account's
 * balance (Import_Domain::refresh_monarch_balances). It is in a menu rather than a button of its own
 * because the nightly fetch keeps balances current, and a refresh is seldom worth asking for. Only
 * offered when the budget is connected; one waiting on a reconnect gets nothing here, and
 * {@link MonarchRefreshStatus} says where to fix it. After a refusal for asking too soon the item stays
 * disabled, counting down, until it may be asked again.
 */
export function MonarchRefreshMenu({ connection }) {
	const dispatch = useDispatch();
	const refreshing = useSelector((state) => (state.importing || {}).refreshingBalances);
	const wait = useSecondsUntil(useSelector((state) => (state.importing || {}).refreshBlockedUntil));
	const [trigger, setTrigger] = useState(null);
	const [opened, setOpened] = useState(false);
	if(!connection || connection.status !== 'connected') {
		return null;
	}
	let label = 'Refresh balances';
	if(refreshing) {
		label = 'Refreshing…';
	} else if(wait > 0) {
		label = `${label} (${wait} s)`;
	}
	const title = 'Asks Monarch for every account balance and records the ones mapped to accounts here.';
	return (
		<div className='accounts_page_menu'>
			<Button
				flat
				small
				className='accounts_page_menu_button'
				aria-label='More'
				data-target='accounts_page_menu'
				onClick={() => setOpened(true)}
				ref={setTrigger}
			>
				<VerticalDots />
			</Button>
			<Dropdown
				id='accounts_page_menu'
				opened={opened}
				attachTo={trigger}
				constrainWidth={false}
				alignment='right'
				items={[{
					content: <span className='refresh_balances_item'><MaterialIcon>sync</MaterialIcon> {label}</span>,
					onClick: () => dispatch(refreshMonarchBalances()),
					disabled: refreshing || wait > 0,
					title,
				}]}
			/>
		</div>
	);
}

/** What the last refresh did, or why it could not: the import page's summary line, and its errors. */
export function MonarchRefreshStatus({ connection }) {
	const importing = useSelector((state) => state.importing || {});
	const wait = useSecondsUntil(importing.refreshBlockedUntil);
	/** With the Monarch panel open below, the line need not name the accounts it lists. */
	const balances = monarchBalanceSummary(importing.monarchBalances, { brief: !!importing.monarchAccounts });

	if(!connection || importing.refreshingBalances) {
		return null;
	}
	/** The server's own "try again in N s" is fixed when it is sent; this one counts. */
	if(importing.refreshBlockedUntil) {
		return wait > 0
			? <p className='red-text monarch_refresh_status'>Balances were refreshed from Monarch moments ago. Try again in {wait} s.</p>
			: null;
	}
	/** First, since a refused token's own message says more than the reconnect note below. */
	if(importing.refreshError) {
		return <p className='red-text monarch_refresh_status'>{importing.refreshError}</p>;
	}
	if(connection.status !== 'connected') {
		return (
			<p className='red-text monarch_refresh_status'>
				Monarch needs signing in again before balances can be refreshed. Reconnect it under
				Settings, Import.
			</p>
		);
	}
	if(balances.error) {
		return <p className='red-text monarch_refresh_status'>{balances.error}</p>;
	}
	if(balances.line) {
		return <p className='grey-text monarch_refresh_status'>{balances.line}</p>;
	}
	return null;
}
